// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {ComponentProps, useContext, useEffect, useMemo, useRef,
    useState} from 'react';
import cn from 'classnames';
import etask from 'zetask';
import {t_generated_proxy, t_order} from '../../../../hola/pub/ui/pro/cp/orders';
import Icon_copy from '../../../../hola/pub/img/hola2023/icons/16/copy.svg';
import Icon_chevron_down from '../../../../hola/pub/img/hola2023/icons/16/chevron_down.svg';
import {Code_snippet, i_highlights, i_proxy} from '../dashboard/code_snippet';
import {Code_snippet2} from '../dashboard/code_snippet2';
import {use_screen} from '../../../../hola/pub/lib/screen_util';
import {use_click_outside} from '../../../../hola/pub/lib/use_click_outside';
import styles from './configure.module.less';
import {download_csv, download_json, use_disappear} from '../dashboard/utils';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import {use_debug_ui} from '../../../../hola/pub/lib/debug';
import Icon_check from '../../../../hola/pub/img/hola2023/icons/8/check.svg';

type t_lang = ComponentProps<typeof Code_snippet>['target'];

type t_lang_titles = {
    // eslint-disable-next-line
    [lang in t_lang]: string;
};

const lang_titles: t_lang_titles = {
    curl: 'CURL',
    go: 'Go',
    nodejs: 'Nodejs',
    php: 'PHP',
    python3: 'Python',
};

interface i_lang_select_props {
    lang: string;
    titles: Record<string, string>
    on_select?: (v: t_lang)=>void;
}
const Lang_select = (props: i_lang_select_props)=>{
    const {lang = 'curl', titles, on_select} = props;
    const [is_open, set_is_open] = useState(false);
    const [selected, set_selected] = useState<string>(lang);
    const select_ref = useRef<HTMLDivElement>();
    use_click_outside(select_ref, ()=>set_is_open(false));
    const toggle = ()=>{ set_is_open(!is_open); };
    const select_lang = l=>{
        set_selected(l);
        set_is_open(false);
        if (on_select)
            on_select(l);
    };
    const languages = Object.keys(titles).filter(l=>l!=selected);
    return <div className={cn(styles.lang_select, is_open && styles.opened)}
      ref={select_ref}>
      <div className={styles.selected} onClick={toggle}>
        <span className={styles.lang}>{titles[selected]}</span>
        <span className={styles.arrow}><Icon_chevron_down/></span>
      </div>
      <ul className={styles.options}>
        {languages.map(l=>{
          return <li key={l} onClick={()=>select_lang(l)}
            className={styles.option}>{titles[l]}</li>;
        })}
      </ul>
    </div>;
};

const placeholders: object_of<string> = {
    login: '_login_',
    host: '_host_',
    password: '_pass_',
    port: '_port_',
    site: 'http://httpbin.org/ip',
};

const highlights_hints = (csv_download, json_download, type, generated_proxies,
  theme)=>
{
    return {
        default: {
            login: <>To get your login info download the&nbsp;
              <button onClick={()=>csv_download(type, generated_proxies)}
                className={theme.color_n700}>
                CSV
              </button> or&nbsp;
              <button onClick={()=>json_download(type, generated_proxies)}
                className={theme.color_n700}>
                JSON
              </button> file below</>,
            password: <>To get the password info download the&nbsp;
              <button onClick={()=>csv_download(type, generated_proxies)}
                className={theme.color_n700}>
                CSV
              </button> or&nbsp;
              <button onClick={()=>json_download(type, generated_proxies)}
                className={theme.color_n700}>
                JSON
              </button> file below</>,
            port: <>To get the port info download the&nbsp;
              <button onClick={()=>csv_download(type, generated_proxies)}
                className={theme.color_n700}>
                CSV
              </button> or&nbsp;
              <button onClick={()=>json_download(type, generated_proxies)}
                className={theme.color_n700}>
                JSON
              </button> file below</>,
            host: <>To get the host info download the&nbsp;
              <button onClick={()=>csv_download(type, generated_proxies)}
                className={theme.color_n700}>
                CSV
              </button> or&nbsp;
              <button onClick={()=>json_download(type, generated_proxies)}
                className={theme.color_n700}>
                JSON
              </button> file below</>,
            country: 'You can change countries easily by adding country ' +
                'code. For example <FR> for France',
            session: 'You can change the number of sessions simply by ' +
                'entering the number of sessions you\'d like',
        },
        example: {
            login: 'You will get your toolip user ID info, which will be ' +
                'created once you click on “Generate proxies”',
            password: 'You will have your password info created for you, ' +
                'once you click on “Generate proxies”',
            port: 'Your port info will be created for you once you click ' +
                'on “Generate proxies”',
            country: 'You can change countries easily by adding country ' +
            'code. For example <FR> for France',
            session: 'You can change the number of sessions simply by ' +
                'entering the number of sessions you\'d like',
            host: 'You will get your host info once you click on "Generate ' +
                'proxies"',
        },
    };
};

interface i_custom_snippet {
    lang_key: string;
    caption: string;
    get_code: ()=>string;
}
interface i_configuration_snippet {
    custom_snippets?: i_custom_snippet[];
    order: t_order;
    generated: Partial<t_generated_proxy>[];
    lang: string;
    example?: boolean;
    with_line_numbers?: boolean;
}
export const Configuration_snippet = (props: i_configuration_snippet)=>{
    const {order, generated, example, with_line_numbers,
        custom_snippets} = props;
    const {theme} = useContext(Settings_context);
    const {width} = use_screen();
    const use_code_snippet2 = use_debug_ui('code_snippet2');
    const data = useMemo(()=>{
        const highlight_hints = highlights_hints(
            download_csv,
            download_json,
            order?.type,
            order?.generated_proxies,
            theme
        );
        if (!order || !generated || !generated[0])
            return {};
        const hints = example ? highlight_hints.example :
          highlight_hints.default;
        const generated_values = generated.reduce((acc, p)=>{
            acc.login.add(p.login);
            acc.password.add(p.password);
            acc.host.add(p.host);
            acc.port.add(p.port);
            return acc;
        }, {host: new Set(), port: new Set(), login: new Set(),
            password: new Set()});
        const proxy = {} as i_proxy;
        const highlights = {} as i_highlights;
        const add_highlight = (k: string, text?: string)=>{
            highlights[k] = {
                text: text||placeholders[k],
                hint: hints[k],
            };
        };
        for (const [k, s] of Object.entries(generated_values))
        {
            proxy[k] = s.values().next().value;
            const need_highlight = (k == 'login' || k == 'password' ||
              k == 'port' || k == 'host') && s.size == 1;
            if (s.size>1 || !proxy[k])
            {
                proxy[k] = placeholders[k];
                add_highlight(k);
            }
            else if (need_highlight)
              add_highlight(k, proxy[k]);
        }
        if (order.unit=='gb')
        {

            proxy.login = generated[0].login
                .replace(/-\w{2}-/, '-XX-')
                .replace(/-[\d\w]+$/, '-###');
            add_highlight('country', 'XX');
            add_highlight('session', '###');
            if (proxy.login.startsWith(placeholders.login))
                add_highlight('login');
        }
        return {proxy, highlights};
    }, [order, generated, example]);
    const [lang, set_lang] = useState<string>(props.lang);
    const [snip, set_snip] = useState('');
    const {visible: copied, show: show_copied} = use_disappear(1);
    const [lines_count, set_lines_count] = useState(null);
    const root_ref = useRef<HTMLDivElement>();
    const code_ref = useRef<HTMLPreElement>();
    const languages = useMemo(()=>{
        const res = {};
        for (const s of custom_snippets ?? [])
            res[s.lang_key] = s.caption;
        return {...res, ...lang_titles};
    }, [custom_snippets]);
    useEffect(()=>{
        if (!root_ref.current || !code_ref.current)
            return;
        root_ref.current.style.setProperty('--code_line_height', '0px');
        const lh = getComputedStyle(code_ref.current).lineHeight;
        const lc = Math.floor(code_ref.current.clientHeight/parseFloat(lh));
        set_lines_count(lc);
        root_ref.current.style.setProperty('--code_line_height', lh);
    }, [root_ref.current, code_ref.current, lang]);
    if (!data.proxy)
        return null;
    const maybe_custom_snipper = custom_snippets?.find(s=>s.lang_key==lang);
    const langs = Object.keys(languages);
    return <div ref={root_ref} className={cn(styles.config_code_wrapper,
      (/insomniac|generic/).test(lang) && styles.generic_insomniac_h)}>
        <div className={styles.config_code}>
          <div className={styles.top_panel}>
            {example ? <div className={styles.languages}>
              <button className={cn(styles.lang_btn,
                styles.active, styles.hovered)}>
                Example {languages[lang]}
              </button>
            </div> : width >= 768 ? <div className={styles.languages}>
              {langs.map(l=>{
                return <button key={l} onClick={()=>set_lang(l)}
                  className={cn(styles.lang_btn, l==lang && styles.active)}>
                  {l==lang && <Icon_check width='8px' />}
                  {languages[l]}
                </button>;
              })}
            </div> : <Lang_select on_select={set_lang} lang={lang}
              titles={languages}/>}
          </div>
          {maybe_custom_snipper ?
            <Custom_proxy_snippet get_code={maybe_custom_snipper.get_code}/>
            : <div className={styles.code_panel}>
              {with_line_numbers && <div className={styles.line_numbers}>
                {lines_count && Array
                  .from({length: lines_count}, (_, i)=>i+1)
                  .map(n=>{ return <span key={n}>{n}</span>; })}
              </div>}
              {use_code_snippet2 ?
                <Code_snippet2
                  target={lang as t_lang}
                  proxy={data.proxy}
                  ref={code_ref}
                  on_text_change={v=>set_snip(v)}
                  highlights={data.highlights}
                />
               :
                <Code_snippet
                  target={lang as t_lang}
                  proxy={data.proxy}
                  ref={code_ref}
                  on_text_change={v=>set_snip(v)}
                  highlights={data.highlights}
                />}
            </div>}
          {!example && <button className={styles.copy_btn}
            onClick={()=>{
              etask(function*(){
                try {
                  yield navigator.clipboard.writeText(
                    maybe_custom_snipper?.get_code() ?? snip);
                  show_copied();
                } catch(e){}
              });
            }}>
            <Icon_copy/> Copy code
            <span className={cn(styles.copied, copied && styles.visible)}>
              Copied!</span>
          </button>}
        </div>
      </div>;
};

export const Custom_proxy_snippet=(props: {get_code: ()=>string})=>{
  return <div className={cn(styles.code_panel, styles.proxy)}>
    <div className={cn(styles.proxy_code)}>
      {props.get_code()}
    </div>
  </div>;
};

export const get_code=(generated: Partial<t_generated_proxy>[],
    sep: string, omit_credentials: boolean, leading_comma = false)=>
{
    return generated.map(p=>[
        ...leading_comma ? [''] : [],
        p.host, p.port, ...omit_credentials ? []:[p.login, p.password]
    ].join(sep)).join('\n');
};

